import React, { useState, useEffect } from "react";

import { useList } from "store/list";
import { useCore } from "store/core";
import { Formik, Field, Form } from "formik";
import {
  TextField,
  Loading,
  MultiSelect,
  Select,
  PersianDataPicker,
} from "components/form";
import {
  generate_uuid,
  dateTimeToserver,
  transformTranslateFromValues,
  transformTranslateServerValues,
} from "lib/helpers";
import { required, validationWrapper, number } from "lib/formValidations";
import PersianDataPickerNew from "components/form/PersianDataPickerNew";

export default function ListFilter(props) {
  const core = useCore();
  const [loadReport, setLoadReport] = useState(false);
  const {
    dispatch,
    clear,
    getList,
    list,
    load,
    createAndDownload,
    remove,
    listSearch,
    from,
    to,
    listFilter,
    total,
    filters,
    dowloadEexelReport,
  } = useList();

  return (
    <div className="intro-y p-2 bg-white dark:bg-dark-3 dark:text-gray-300 grid col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2">
      <h3 className="intro-y text-sm font-medium col-span-12 my-2">فیلترها</h3>
      <Formik
        initialValues={filters}
        onSubmit={async (values, { setSubmitting }) => {
          const enc = (v) => encodeURIComponent(String(v));
          const toPrimitive = (x) => {
            if (x == null) return x;
            if (typeof x === "object" && "value" in x) return x.value;
            return x;
          };
          const normalizeFilterValue = (v, field) => {
            const p = toPrimitive(v);
            if (p === "" || p == null) return p;
            const raw = String(p);
            const prefixMatch = raw.match(
              new RegExp(`^${field}-\\$?(gte|lte|gt|lt|between)-`, "i")
            );
            return prefixMatch ? raw.slice(prefixMatch[0].length) : raw;
          };
          const toArray = (v) => {
            if (Array.isArray(v))
              return v.map(toPrimitive).filter((x) => x !== "" && x != null);
            const p = toPrimitive(v);
            return p === "" || p == null ? [] : [p];
          };
          const isJsonLike = (s) => {
            if (!s) return false;
            const t = s.trim();
            return t.startsWith("{") && t.endsWith("}");
          };

          const parts = [];

          const raw = (values.filter || "").replace(/^filter:/, "").trim();
          const cleanedRaw = isJsonLike(raw)
            ? raw.trim()
            : raw
                .split(",")
                .map((s) => s.trim())
                .filter(
                  (item) =>
                    item &&
                    !item.startsWith("createdAt-$gte-") &&
                    !item.startsWith("createdAt-$lte-") &&
                    !item.startsWith("createdAt-$between-")
                )
                .join(",");

          const from = normalizeFilterValue(values.filter_from, "createdAt");
          const to = normalizeFilterValue(values.filter_to, "createdAt");
          if (from && to) {
            parts.push(`createdAt-$between-${enc(from)}|${enc(to)}`);
          } else if (from) {
            parts.push(`createdAt-$gte-${enc(from)}`);
          } else if (to) {
            parts.push(`createdAt-$lte-${enc(to)}`);
          }

          const pFrom = values.price_from;
          const pTo = values.price_to;
          if (pFrom && pTo) {
            parts.push(`price-$between-${enc(pFrom)}|${enc(pTo)}`);
          } else if (pFrom) {
            parts.push(`price-$gte-${enc(pFrom)}`);
          } else if (pTo) {
            parts.push(`price-$lte-${enc(pTo)}`);
          }

          (props.items || []).forEach((item, index) => {
            const name = item?.name;
            if (!name) return;

            if (
              [
                "filter",
                "filter_from",
                "filter_to",
                "date_from",
                "date_to",
                "price_from",
                "price_to",
              ].includes(name)
            )
              return;

            if (
              ["DATE_FROM_TO", "PRICE_FROM_TO", "ADVANCED_FILTER"].includes(
                item.type
              )
            )
              return;

            const val = values[name];
            if (
              val === undefined ||
              val === null ||
              val === "" ||
              (Array.isArray(val) && val.length === 0)
            )
              return;

            if (item.type === "FILTER") {
              const arr = toArray(val);
              if (arr.length > 0) {
                arr.forEach((tok) => parts.push(String(tok)));
              }
              return;
            }

            const arr = toArray(val);
            if (arr.length === 0) return;

            if (Array.isArray(val)) {
              const joined = arr.map((x) => enc(x)).join("|");
              parts.push(`${name}-$in-${joined}`);
            } else {
              const op = item.operator || "$eq";
              parts.push(`${name}-${op}-${enc(arr[0])}`);
            }
          });

          let filterString = "";
          if (isJsonLike(raw)) {
            filterString = raw.trim();
          } else {
            const uiTokens = [...parts];
            const rawTokens = cleanedRaw
              ? cleanedRaw
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [];

            const byPrefix = {};
            // raw first
            rawTokens.forEach((t) => {
              const pfx = String(t).split("-").slice(0, 2).join("-");
              byPrefix[pfx] = t;
            });
            // then UI overrides raw
            uiTokens.forEach((t) => {
              const pfx = String(t).split("-").slice(0, 2).join("-");
              byPrefix[pfx] = t;
            });

            filterString = Object.values(byPrefix).join(",");
          }

          const payload = {};
          const formKeys = [
            ...(props.items || []).map((i) => i.name).filter(Boolean),
            "filter",
            "filter_from",
            "filter_to",
            "price_from",
            "price_to",
          ];
          Object.keys(filters || {}).forEach((k) => {
            if (!formKeys.includes(k)) {
              payload[k] = filters[k];
            }
          });

          if (filterString) {
            payload.filter = filterString;
          }

          await dispatch(listFilter(props.path, payload));
          setSubmitting(false);
        }}
        enableReinitialize
      >
        {({ isSubmitting, dirty, values, setFieldValue }) => (
          <Form className="grid grid-cols-12 gap-x-2 col-span-12">
            <>
              {props.items &&
                props.items.length > 0 &&
                props.items.map((item, index) => {
                  switch (item.type) {
                    case "FILTER":
                    case "SELECT":
                      return (
                        <div
                          key={`item_${index}_formik`}
                          className="mt-2 col-span-12 sm:col-span-6 lg:col-span-4"
                        >
                          <Field
                            placeholder={item.label}
                            label={item.label + ":"}
                            name={item.name}
                            component={Select}
                            options={item.options}
                          />
                        </div>
                      );
                    case "DATE_FROM_TO":
                      return (
                        <div
                          key={`item_${index}`}
                          className="mt-2 col-span-12 sm:col-span-6 lg:col-span-4 flex justify-center gap-x-1 items-end"
                        >
                          <div className="w-full sm:w-56 relative text-gray-700 dark:text-gray-300">
                            <Field
                              placeholder="از تاریخ"
                              label="از تاریخ :"
                              name="filter_from"
                              operator="$gte"
                              component={PersianDataPickerNew}
                              disable_today={true}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue("filter_from", null);
                              setFieldValue("filter_to", null);
                            }}
                            className="mr-2 ml-2"
                          >
                            <svg
                              height="20"
                              width="20"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                              focusable="false"
                              className="rounded-full bg-[#ccc5c5] hover:bg-[#bdb6b6] fill-[#ec4d4d] transition-colors duration-200 relative top-[-11px]"
                            >
                              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l-2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c-0.469 0.469-0.469 1.229 0 1.698z" />
                            </svg>
                          </button>
                          <div className="w-full sm:w-56 relative text-gray-700 dark:text-gray-300">
                            <Field
                              placeholder="تا تاریخ"
                              label="تا تاریخ :"
                              name="filter_to"
                              operator="$lte"
                              component={PersianDataPickerNew}
                              disable_today={true}
                            />
                          </div>
                        </div>
                      );
                    case "ADVANCED_FILTER":
                      return null;
                    case "TEXT":
                      return (
                        <div
                          key={`item_${index}`}
                          className="mt-2 col-span-12 sm:col-span-6 lg:col-span-4"
                        >
                          <Field
                            placeholder={item.label}
                            label={item.label + ":"}
                            name={item.name}
                            component={TextField}
                          />
                        </div>
                      );
                    case "PRICE_FROM_TO":
                      return (
                        <div
                          key={`item_${index}`}
                          className="mt-2 col-span-12 sm:col-span-6 lg:col-span-4 flex justify-center items-end"
                        >
                          <Field
                            placeholder="مبلغ از"
                            label={"مبلغ از:"}
                            name="price_from"
                            component={TextField}
                            type="number"
                            validate={(value) =>
                              validationWrapper(value, number)
                            }
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue("price_to", "");
                              setFieldValue("price_from", "");
                            }}
                          >
                            <svg
                              height="20"
                              width="20"
                              viewBox="0 0 20 20"
                              aria-hidden="true"
                              focusable="false"
                              className="css-tj5bde-Svg"
                            >
                              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l-2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c-0.469 0.469-0.469 1.229 0 1.698z"></path>
                            </svg>
                          </button>
                          <Field
                            placeholder="مبلغ تا"
                            label={"مبلغ تا:"}
                            name="price_to"
                            component={TextField}
                            type="number"
                            validate={(value) =>
                              validationWrapper(value, number)
                            }
                          />
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              <div className="mt-2 col-span-12 flex justify-between sm:justify-end gap-x-3 mt-3">
                <button
                  className="btn btn-primary shadow-md w-full sm:w-56  sm:dark:text-gray-300 md:w-auto"
                  type="submit"
                  disabled={!!(isSubmitting || !dirty)}
                >
                  {isSubmitting ? <Loading /> : "اعمال"}
                </button>
                {props.hasExcelReport && (
                  <button
                    className="btn btn-primary shadow-md w-full sm:w-56  sm:dark:text-gray-300 md:w-auto"
                    type="button"
                    disabled={loadReport}
                    onClick={async () => {
                      setLoadReport(true);
                      let values_t = values;
                      if (values.date_from)
                        values_t.date_from = dateTimeToserver(
                          values.date_from,
                          "YYYY-MM-DD"
                        );
                      if (values.date_to)
                        values_t.date_to = dateTimeToserver(
                          values.date_to,
                          "YYYY-MM-DD"
                        );
                      await dispatch(dowloadEexelReport(props.path, values_t));
                      setLoadReport(false);
                    }}
                  >
                    {isSubmitting ? <Loading /> : "دریافت اکسل"}
                  </button>
                )}
              </div>
            </>
          </Form>
        )}
      </Formik>
    </div>
  );
}
